import {IMidiNotePlayed} from "../eventBus/midi/MidiEventTypes";
import {subscribeToMidiEvents} from "../eventBus/midi/midiEventBus";
import settingsService from "../settings/settingsService";
import {displayErrorToUser} from "../error/error";
import {logger} from "../log/logger";
import {commonStore} from "../../redux/common/commonStore";
import {keyboardWizardStore} from "../../redux/keyboardWizard/keyboardWizardStore";
import {KeyboardWizardPage} from "../../redux/keyboardWizard/keyboardWizardTypes";

class SetKeyboardSizeLogic {
  #unsubscribeFuncs: (() => void)[] = [];
  #isSubscribedToMidiEvents = false;

  destroy() {
    this.#unsubscribeFuncs.forEach(f => f());
  }

  setUpTheKeyboard() {
    if (!this.#isSubscribedToMidiEvents) {
      const unsubscribe = subscribeToMidiEvents((midiNotePlayed) => {
        switch (midiNotePlayed.type) {
          case "midiNotePlayed":
            return this.onMidiNote_pressed(midiNotePlayed);
        }
      }, `SetKeyboardSize`);

      const unsubscribeFunc = () => {
        unsubscribe();
        this.#isSubscribedToMidiEvents = false;
      };

      this.#unsubscribeFuncs.push(unsubscribeFunc);
      this.#isSubscribedToMidiEvents = true;
    } else {
      keyboardWizardStore.setCurrentPage(KeyboardWizardPage.SetLowestNote);
    }
  }

  setLowestNote(midiNotePlayed: IMidiNotePlayed): void {
    keyboardWizardStore.setLowestNote(midiNotePlayed.number);
    keyboardWizardStore.setCurrentPage(KeyboardWizardPage.SetHighestNote);
  }

  onMidiNote_pressed(midiNotePlayed: IMidiNotePlayed) {
    // active = the device that sent the midi note
    keyboardWizardStore.setActiveDevice({
      activeDeviceId: midiNotePlayed.normalizedMidiInput.getId(),
      activeDeviceName: midiNotePlayed.normalizedMidiInput.getName(),
    });

    // Guard: make sure input is coming from the expected device
    if (!this.isInputFromCorrectDevice(midiNotePlayed)) {
      if (keyboardWizardStore.getCanShowInputDetectedFromWrongDeviceModal()) {
        keyboardWizardStore.setShowInputDetectedFromWrongDeviceModal(true);
        return;
      } else {
        return;
      }
    }

    // If an error is currently being displayed from a previous attempt to configure the keyboard, hide it.
    commonStore.hideError();

    const deviceId = midiNotePlayed.normalizedMidiInput.getId();
    const deviceName = midiNotePlayed.normalizedMidiInput.getName();

    if (keyboardWizardStore.getCurrentPage() === KeyboardWizardPage.SetLowestNote) {
      this.setLowestNote(midiNotePlayed);
      return;
    } else if (keyboardWizardStore.getCurrentPage() === KeyboardWizardPage.SetHighestNote) {
      const highestNote = midiNotePlayed.number;
      const lowestNote = keyboardWizardStore.getLowestNote();

      if (lowestNote === null) {
        logger.deviceKeyboard.error({message: `error: no lowest number defined but somehow got to second step`});
        return;
      }

      if (highestNote <= lowestNote) {
        displayErrorToUser("Leftmost MIDI note and rightmost MIDI note cannot be the same. Please try again starting with the leftmost MIDI note.");
        keyboardWizardStore.setCurrentPage(KeyboardWizardPage.SetLowestNote);
        return;
      }

      const numberOfKeys = highestNote - lowestNote + 1;

      settingsService.changeMidiDeviceSettings({
        id: deviceId,
        name: deviceName,
        dateTimeConfiguredMS: Date.now(),
        numberOfKeys: numberOfKeys,
        lowestNote: lowestNote,
        highestNote: highestNote,
      });

      this.destroy();
      settingsService.setCurrentlySelectedMidiDevice(deviceId, deviceName);
      keyboardWizardStore.setCurrentPage(KeyboardWizardPage.SetupComplete);

    } else {
      logger.deviceKeyboard.error({message: `Should not be able to get here`});
    }
  }

  isInputFromCorrectDevice(midiNotePlayed: IMidiNotePlayed): boolean {
    const deviceToConfigureId = keyboardWizardStore.getDeviceToConfigureId();
    return midiNotePlayed.normalizedMidiInput.getId() === deviceToConfigureId?.toString();
  }
}

const setKeyboardSizeLogic = new SetKeyboardSizeLogic();
export default setKeyboardSizeLogic;
